import {
  DefaultInputState,
  InputComponentProps,
  InputProps,
  InputState,
} from '@bizapp-lp/workflow/organisms/InputFormType';
import React, { ReactNode } from 'react';
import {
  convertFullwidthChar,
  convertionTargetTypes,
  helperTextFactory,
  validationFuncFactory,
  ValidationType,
} from '@bizapp-lp/workflow/molecules/Validation';
import * as lodash from 'lodash';

export type FormState = Map<string, InputState>;
export type FormDataChangeHandler = (
  changedInputId: string,
  newFormState: FormState,
) => void;

export type InputValueChangeHandler = (newValue: any) => void;
export type ValidateHandler = (valid: boolean) => void;

interface InputFormProps {
  // input configuration
  inputConfigs: InputProps[];
  // form state offer by every page component
  // which contains value, validation result(error&warning) see `InputState`
  formState: FormState;
  // callback to return input changes
  onChange?: FormDataChangeHandler;
  // callback to return onblur changes
  onBlur?: FormDataChangeHandler;
  // callback to return validation results
  onValidate?: ValidateHandler;
  // offer methods to apply customized attributes for specified components
  // e.g. file uploader
  customizeComponentProps?: (
    // input configuration
    inputConfig: InputProps,
    // transferred component props
    inputComponentProps: InputComponentProps,
    // input form onChangeHandler, call this to invoke the default onChange process
    onChangeHandler: InputValueChangeHandler,
    // input form onBlurHandler,  call this to invoke the default onBlur process
    onBlurHandler: InputValueChangeHandler,
  ) => InputComponentProps;
  // Input form offer the finally generated input component props to the children
  children: (inputComponentPropsList: InputComponentProps[]) => ReactNode;
}

// default function which doesn't modify any props
export const identity = (componentProps: InputComponentProps) => componentProps;

// check states of all inputs of ids return true if all inputs are valid
const isStateReady = (fullState: Map<string, InputState>, ids: string[]) =>
  ids.reduce<boolean>((acc, id) => {
    const state = fullState.get(id) ?? DefaultInputState;
    const ok =
      !state.error &&
      !state.warning &&
      (!state.required || (state.value !== '' && state.value !== null));
    return acc && ok;
  }, true);

// validation
const validate = (
  value: any,
  required: boolean,
  validationType?: ValidationType,
) => {
  const isValid = validationFuncFactory(validationType);
  return required
    ? isValid(value) && value !== ''
    : isValid(value) || value === '';
};

type OnChangeHandler = (
  id: string,
  newValue: any,
  required: boolean,
  validationType?: ValidationType,
) => void;
type OnBlurHandler = OnChangeHandler;

const toInputComponentList = (
  inputConfigs: InputProps[],
  formState: FormState,
  onChangeHandler: OnChangeHandler,
  onBlurHandler: OnBlurHandler,
  applyExtraProps: (
    inputProps: InputProps,
    inputComponentProps: InputComponentProps,
    onChangeHandler: InputValueChangeHandler,
    onBlurHandler: InputValueChangeHandler,
  ) => InputComponentProps = (_, componentProps, ..._props) =>
    identity(componentProps),
): InputComponentProps[] => {
  // convert inputConfigs to inputComponentProps
  // merge config, formState and event callbacks
  return inputConfigs.map((inputConfig) => {
    const { logicOptions, ...props } = inputConfig;
    const validationType = logicOptions?.validationType;
    const required = props.required ?? false;
    const componentProps = {
      ...props,
      ...formState.get(props.id),
      helperText: inputConfig.helperText ?? helperTextFactory(validationType),
      onChange: (newValue: any) =>
        onChangeHandler(props.id, newValue, required, validationType),
      onBlur: (newValue: any) =>
        onBlurHandler(props.id, newValue, required, validationType),
    };
    return applyExtraProps(
      inputConfig,
      componentProps,
      (newValue: any) =>
        onChangeHandler(props.id, newValue, required, validationType),
      (newValue: any) =>
        onBlurHandler(props.id, newValue, required, validationType),
    );
  });
};

// InputForm
// support
// 1. transfer input config to input component props
// 2. apply onChange & onBlur to the component to deal with validation and data updates
// 3. can customize input props with `customizeComponentProps`
export const InputForm: React.FC<InputFormProps> = ({
  inputConfigs,
  formState,
  onChange = () => {},
  onBlur = () => {},
  onValidate = () => {},
  customizeComponentProps = (_, componentProps, ..._props) =>
    identity(componentProps),
  children,
}) => {
  // handle onChange of input
  const handleOnChange = (
    id: string,
    newValue: any,
    required: boolean,
    validationType?: ValidationType,
  ): FormState => {
    // validation new value
    const valid = validate(newValue, required, validationType);
    // update date and validation
    const newFormState = lodash.clone(formState);
    newFormState.set(id, {
      ...(newFormState.get(id) ?? DefaultInputState),
      value: newValue,
      error: !valid,
      warning: false,
    });
    // call onChange callback
    // form state is controlled by the outer component
    // update inner inputs by refer **finally** updates from formState from props
    // so that in the outer component extract business logic can have the full control of the finally states
    onChange(id, newFormState);
    return newFormState;
  };

  // handle onBlur of input
  const handleOnBlur = (
    id: string,
    crrValue: any,
    required: boolean,
    validationType?: ValidationType,
  ) => {
    let newValue = crrValue;
    let newFormState;
    // convert full width character and call `handleOnChange`
    if (convertionTargetTypes.includes(validationType)) {
      newValue = convertFullwidthChar(crrValue);
      newFormState = handleOnChange(id, newValue, required, validationType);
    } else {
      // or just do validation

      // validation new value
      const valid = validate(newValue, required, validationType);
      // update date and validation
      newFormState = lodash.cloneDeep(formState);
      newFormState.set(id, {
        ...(newFormState.get(id) ?? DefaultInputState),
        error: !valid,
      });
    }

    // call onBlur callback
    onBlur(id, newFormState);
  };

  // form validation status (i.e. all inputs is valid) updated when `formState` changed
  // it acts as a helper function
  // since all validation status of inputs can be checked from the source of `formStata` directly
  React.useEffect(() => {
    const isFormValid = isStateReady(
      formState,
      inputConfigs.map(({ id }) => id),
    );
    onValidate(isFormValid);
  });

  const componentPropsList: InputComponentProps[] = toInputComponentList(
    inputConfigs,
    formState,
    handleOnChange,
    handleOnBlur,
    customizeComponentProps,
  );
  return <>{children(componentPropsList)}</>;
};

// initialization helper function
export const calcInitialState = (inputList: InputProps[]) => {
  return new Map(
    inputList
      .filter((v) => {
        return v.kind !== 'sectionTitle';
      })
      .map((v) => {
        let value;
        switch (v.kind) {
          case 'checkboxGroup':
            value = new Map(
              Array.from(v.items?.entries() ?? []).map(([key, _]) => [
                key,
                false,
              ]),
            );
            break;
          case 'fileupload':
            value = null as File | null;
            break;
          default:
            // set other input default value to ''
            value = v.value ?? '';
        }

        const base = {
          ...DefaultInputState,
          id: v.id,
          value: value,
          required: !(v.disabled || v.hidden) && (v.required ?? false),
          disabled: v.disabled ?? false,
          hidden: v.hidden ?? false,
        };
        return [v.id, base];
      }),
  );
};
